import React, { useEffect, useMemo } from "react";
import { ChevronDown, MapPin } from "lucide-react";
import { usePremisesStore, useRegionStore } from "../../store/store";

interface DropdownOption {
  id: string;
  name: string;
}

interface DropdownProps {
  selectedValue: string;
  onChange: (value: string) => void;
  type: "region" | "premises";
  selectedRegion?: string;
  disabled?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  selectedValue,
  onChange,
  type,
  selectedRegion,
  disabled = false,
}) => {

  const {
    premiseOptions,
    fetchPremiseOptions,
    optionsLoading: premiseOptionsLoading,
  } = usePremisesStore();

  const { regions, fetchRegions, loading: regionsLoading } = useRegionStore();

  useEffect(() => {
    if (type === "region" && regions.length === 0) {
      fetchRegions();
    }
    if (type === "premises" && premiseOptions.length === 0) {
      fetchPremiseOptions();
    }
  }, [
    type,
    regions.length,
    premiseOptions.length,
    fetchRegions,
    fetchPremiseOptions,
  ]);

  const options = useMemo<DropdownOption[]>(() => {
    if (type === "region") {
      return regions
        .map((region: any) => ({
          id: String(region._id ?? region.id ?? ""),
          name: String(region.regionName ?? "").trim(),
        }))
        .filter((option) => option.id !== "" && option.name !== "");
    }

    return (premiseOptions)
      .filter(
        (premise) =>
          !selectedRegion ||
          String(premise.region ?? "") === String(selectedRegion)
      )
      .map((premise) => ({
        id: String(premise.id),
        name: premise.premisesName.trim(),
      }));

  }, [type, regions, premiseOptions, selectedRegion]);

  const isLoading =
    type === "region" ? regionsLoading : premiseOptionsLoading;

  return (
    <span className="flex items-center">
      <div className="relative flex w-48 items-center rounded-lg border-2 border-[#a5b7cf] bg-white px-2 py-2">
        <MapPin className="h-5 w-5 shrink-0 text-[#61748e]" />
        <select
          aria-label={type === "region" ? "Filter by region" : "Filter by premise"}
          className="flex-1 appearance-none bg-transparent px-3 text-md font-medium text-[#5a687c] outline-none disabled:cursor-not-allowed disabled:opacity-50"
          value={selectedValue}
          disabled={disabled || isLoading}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">
            {type === "region" ? "All Regions" : "All Premises"}
          </option>

          {options.map((option) => (
            <option
              key={option.id}
              value={option.id}
              className="text-[#5a687c]"
            >
              {option.name}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 h-4 w-4 text-gray-500" />
      </div>
    </span>
  );
};
